import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, ApiError, type PricedCart } from '../lib/api'
import { useCart, useStore } from '../lib/store'
import { Img, Loading, Shell } from '../components/Layout'

/** Re-prices the bag on the server whenever its contents, or the referral code, change. */
export function usePricedCart(code = '') {
  const { items } = useCart()
  const [priced, setPriced] = useState<PricedCart | null>(null)
  const [loading, setLoading] = useState(items.length > 0)
  const [error, setError] = useState('')

  const key = JSON.stringify([items, code])

  useEffect(() => {
    if (items.length === 0) {
      setPriced(null)
      setLoading(false)
      return
    }
    let alive = true
    setLoading(true)
    setError('')
    api
      .priceCart(items, code)
      .then((p) => alive && setPriced(p))
      .catch((e) => alive && setError(e instanceof ApiError ? e.message : 'Could not price your bag.'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return { priced, loading, error }
}

export default function Cart() {
  const { items, setQty, remove } = useCart()
  const { money, settings } = useStore()
  const { priced, loading, error } = usePricedCart()
  const navigate = useNavigate()

  const empty = items.length === 0

  return (
    <Shell>
      <section className="section">
        <h1 className="display h-md" style={{ textAlign: 'center', marginBottom: 'clamp(24px,3vw,40px)' }}>
          Your bag
        </h1>

        {empty ? (
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, padding: '30px 0' }}>
            <p className="small" style={{ margin: 0 }}>
              Your bag is empty.
            </p>
            <Link to="/shop" className="btn">
              Shop the range
            </Link>
          </div>
        ) : loading && !priced ? (
          <Loading label="Pricing your bag" />
        ) : error ? (
          <div className="notice notice-error">{error}</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.6fr) minmax(260px,1fr)', gap: 'clamp(20px,3vw,44px)', alignItems: 'start' }} className="section-split">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {priced?.problems.length ? (
                <div className="notice notice-error" style={{ marginBottom: 16 }}>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {priced.problems.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {priced?.items.map((line) => (
                <div key={line.slug} style={{ display: 'grid', gridTemplateColumns: '90px minmax(0,1fr) auto', gap: 16, alignItems: 'center', padding: '16px 0', borderBottom: '1px solid var(--line)' }}>
                  <Link to={`/product/${line.slug}`} style={{ display: 'block', aspectRatio: '4 / 5', background: 'var(--stone)', overflow: 'hidden' }}>
                    <Img src={line.image} alt={line.name} label={line.name} />
                  </Link>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                    <Link to={`/product/${line.slug}`} style={{ fontSize: 13, fontWeight: 500 }}>
                      {line.name}
                    </Link>
                    <span className="small">{money(line.unitPrice)} each</span>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <div style={{ display: 'flex', border: '1px solid var(--line-2)' }}>
                        <button onClick={() => setQty(line.slug, line.qty - 1)} aria-label={`Decrease ${line.name}`} style={{ width: 30, border: 'none', background: 'none', cursor: 'pointer' }}>
                          −
                        </button>
                        <span style={{ width: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5 }}>{line.qty}</span>
                        <button onClick={() => setQty(line.slug, line.qty + 1)} aria-label={`Increase ${line.name}`} style={{ width: 30, border: 'none', background: 'none', cursor: 'pointer' }}>
                          +
                        </button>
                      </div>
                      <button onClick={() => remove(line.slug)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, letterSpacing: '.1em', color: 'var(--muted)', textDecoration: 'underline', padding: 0 }}>
                        Remove
                      </button>
                    </div>
                  </div>

                  <div style={{ fontSize: 13.5, letterSpacing: '.04em', whiteSpace: 'nowrap' }}>{money(line.linePrice)}</div>
                </div>
              ))}
            </div>

            <aside style={{ background: 'var(--stone)', padding: 'clamp(20px,2.4vw,30px)', display: 'flex', flexDirection: 'column', gap: 14, position: 'sticky', top: 20 }}>
              <h2 style={{ margin: 0, fontSize: 10.5, letterSpacing: '.2em', textTransform: 'uppercase', color: 'var(--muted)' }}>Summary</h2>
              <Row label="Subtotal" value={money(priced?.subtotal ?? 0)} />
              <Row label="Delivery" value={priced?.shipping ? money(priced.shipping) : 'Free'} />
              {settings && priced && priced.subtotal < settings.freeShippingThreshold && (
                <p className="small" style={{ margin: 0, lineHeight: 1.7 }}>
                  Spend {money(settings.freeShippingThreshold - priced.subtotal)} more for complimentary delivery.
                </p>
              )}
              <div style={{ borderTop: '1px solid var(--line-2)', paddingTop: 14, display: 'flex', justifyContent: 'space-between', fontSize: 15 }}>
                <span>Total</span>
                <span style={{ fontFamily: 'var(--serif)', fontSize: 22 }}>{money(priced?.total ?? 0)}</span>
              </div>
              <button
                className="btn btn-solid"
                style={{ padding: '14px 24px' }}
                disabled={!priced || priced.items.length === 0 || priced.problems.length > 0}
                onClick={() => navigate('/checkout')}
              >
                Checkout
              </button>
              <Link to="/shop" className="btn btn-sm" style={{ textAlign: 'center' }}>
                Continue shopping
              </Link>
            </aside>
          </div>
        )}
      </section>
    </Shell>
  )
}

export function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--muted)' }}>
      <span>{label}</span>
      <span style={{ color: 'var(--ink)' }}>{value}</span>
    </div>
  )
}
